import { useState } from 'react';
import { api, type CatalogPlugin, type InstanceAssignments, type SkillPack } from '../../api';
import { useAction, useLoader } from '../../hooks';
import { compareSemver } from '../../semver';
import { Alert } from '../Alert';
import { ConfirmDialog } from '../ConfirmDialog';
import { RISK_LABELS, RISK_TONES, VERSION_STATUS_LABELS } from '../labels';
import { latestPublished } from '../PluginsTab';
import { Tag } from '../Tag';

const REF_RE = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,99}$/;

type PluginAssignment = InstanceAssignments['plugins'][number];
type PackAssignment = InstanceAssignments['skillPacks'][number];

// --- plugins --------------------------------------------------------------------------------------------------------

function PluginRow({
  plugin,
  assignment,
  busy,
  onSave,
  onRemove,
}: {
  plugin: CatalogPlugin;
  assignment: PluginAssignment | undefined;
  busy: boolean;
  onSave: (enabled: boolean, pinnedVersion: string | null) => void;
  onRemove: () => void;
}) {
  const initialEnabled = assignment?.enabled ?? plugin.enabledByDefault;
  const initialPinned = assignment?.pinnedVersion ?? '';
  const [enabled, setEnabled] = useState(initialEnabled);
  const [pinned, setPinned] = useState(initialPinned);
  const latest = latestPublished(plugin);
  const versions = plugin.versions
    .filter((v) => v.status !== 'withdrawn')
    .sort((a, b) => compareSemver(b.version, a.version));
  // Une version épinglée désormais retirée reste affichée pour ne pas être remplacée en silence.
  const known = new Set(versions.map((v) => v.version));
  const orphan = initialPinned && !known.has(initialPinned) ? initialPinned : null;
  const dirty = !assignment || enabled !== initialEnabled || pinned !== initialPinned;

  return (
    <tr>
      <th scope="row" className="user-cell">
        <div>{plugin.title}</div>
        <div className="muted">
          <code>{plugin.package}</code>
        </div>
      </th>
      <td>
        <Tag tone={RISK_TONES[plugin.risk]}>{RISK_LABELS[plugin.risk]}</Tag>
      </td>
      <td>
        {assignment ? (
          <Tag tone="green">Attribué</Tag>
        ) : plugin.enabledByDefault ? (
          <Tag tone="gray">Par défaut : activé</Tag>
        ) : (
          <Tag tone="gray">Non servi</Tag>
        )}
      </td>
      <td>
        <label className="check">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Activé <span className="sr-only">: {plugin.package}</span>
        </label>
      </td>
      <td>
        <label className="sr-only" htmlFor={`pin-${plugin.id}`}>
          Version épinglée de {plugin.package}
        </label>
        <select id={`pin-${plugin.id}`} value={pinned} onChange={(e) => setPinned(e.target.value)}>
          <option value="">Dernière publiée{latest ? ` (${latest.version})` : ' (aucune)'}</option>
          {orphan && <option value={orphan}>{orphan} (retirée du catalogue)</option>}
          {versions.map((v) => (
            <option key={v.id} value={v.version}>
              {v.version}
              {v.status !== 'published' ? ` — ${VERSION_STATUS_LABELS[v.status].toLowerCase()}` : ''}
            </option>
          ))}
        </select>
      </td>
      <td>
        <div className="row-actions">
          <button
            type="button"
            className="btn btn-small"
            disabled={busy || !dirty}
            onClick={() => onSave(enabled, pinned === '' ? null : pinned)}
          >
            {assignment ? 'Enregistrer' : 'Attribuer'} <span className="sr-only">{plugin.package}</span>
          </button>
          {assignment && (
            <button
              type="button"
              className="btn btn-small btn-danger-outline"
              disabled={busy}
              onClick={onRemove}
            >
              Retirer l'attribution <span className="sr-only">de {plugin.package}</span>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// --- skills ---------------------------------------------------------------------------------------------------------

function PackRow({
  pack,
  assignment,
  busy,
  onSave,
  onRemove,
}: {
  pack: SkillPack;
  assignment: PackAssignment | undefined;
  busy: boolean;
  onSave: (enabled: boolean, pinnedRef: string | null) => void;
  onRemove: () => void;
}) {
  const initialEnabled = assignment?.enabled ?? false;
  const initialRef = assignment?.pinnedRef ?? '';
  const [enabled, setEnabled] = useState(pack.mandatory ? true : initialEnabled);
  const [ref, setRef] = useState(initialRef);
  const [error, setError] = useState<string | null>(null);
  const dirty = !assignment || (!pack.mandatory && enabled !== initialEnabled) || ref !== initialRef;
  const refId = `ref-${pack.id}`;

  function save() {
    const value = ref.trim();
    if (value && (!REF_RE.test(value) || value.includes('..'))) {
      setError('Référence Git invalide.');
      return;
    }
    setError(null);
    onSave(pack.mandatory ? true : enabled, value === '' ? null : value);
  }

  return (
    <tr>
      <th scope="row" className="user-cell">
        <div>{pack.name}</div>
        <div className="muted">
          <code>{pack.namespace}</code> · <code>{pack.ref}</code>
        </div>
      </th>
      <td>
        {pack.mandatory ? (
          <Tag tone="orange">Obligatoire</Tag>
        ) : assignment ? (
          <Tag tone="green">Attribué</Tag>
        ) : (
          <Tag tone="gray">Non servi</Tag>
        )}
      </td>
      <td>
        <label className="check">
          <input
            type="checkbox"
            checked={pack.mandatory ? true : enabled}
            disabled={pack.mandatory}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Activé <span className="sr-only">: pack {pack.name}</span>
        </label>
        {pack.mandatory && <div className="muted">toujours servi</div>}
      </td>
      <td>
        <label className="sr-only" htmlFor={refId}>
          Référence épinglée du pack {pack.name}
        </label>
        <input
          id={refId}
          type="text"
          value={ref}
          placeholder={`${pack.ref} (défaut du pack)`}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${refId}-err` : undefined}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setRef(e.target.value)}
        />
        {error && (
          <span id={`${refId}-err`} className="field-error-inline" role="alert">
            {error}
          </span>
        )}
      </td>
      <td>
        <div className="row-actions">
          <button type="button" className="btn btn-small" disabled={busy || !dirty} onClick={save}>
            {assignment ? 'Enregistrer' : 'Attribuer'} <span className="sr-only">le pack {pack.name}</span>
          </button>
          {assignment && (
            <button
              type="button"
              className="btn btn-small btn-danger-outline"
              disabled={busy}
              onClick={onRemove}
            >
              Retirer l'attribution <span className="sr-only">du pack {pack.name}</span>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// --- panneau --------------------------------------------------------------------------------------------------------

export function AssignmentsPanel({ instanceId, onChanged }: { instanceId: string; onChanged: () => void }) {
  const plugins = useLoader(api.plugins);
  const packs = useLoader(api.skillPacks);
  const assignments = useLoader(() => api.assignments(instanceId), instanceId);
  const action = useAction();
  const [removing, setRemoving] = useState<
    { kind: 'plugin'; plugin: CatalogPlugin } | { kind: 'pack'; pack: SkillPack } | null
  >(null);

  const error = plugins.error ?? packs.error ?? assignments.error;
  const a = assignments.data;
  const pluginBy = new Map((a?.plugins ?? []).map((x) => [x.pluginId, x]));
  const packBy = new Map((a?.skillPacks ?? []).map((x) => [x.skillPackId, x]));

  async function put(fn: () => Promise<InstanceAssignments>) {
    const next = await action.run(fn);
    if (next) {
      assignments.setData(next);
      onChanged();
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    const target = removing;
    const ok = await action.run(async () => {
      if (target.kind === 'plugin') await api.removePluginAssignment(instanceId, target.plugin.id);
      else await api.removeSkillPackAssignment(instanceId, target.pack.id);
      return true;
    });
    setRemoving(null);
    if (ok) {
      await assignments.reload();
      onChanged();
    }
  }

  return (
    <div className="stack">
      {error && <Alert>{error}</Alert>}
      {action.error && <Alert>{action.error}</Alert>}

      <h3>Plugins</h3>
      {plugins.data === null && !plugins.error && (
        <p className="muted" aria-busy="true">
          Chargement du catalogue…
        </p>
      )}
      {plugins.data?.length === 0 && (
        <p className="muted">
          Le catalogue de plugins est vide : ajoutez-en dans l'onglet « Plugins » de l'administration.
        </p>
      )}
      {plugins.data && plugins.data.length > 0 && a && (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Plugins attribués à l'instance</caption>
            <thead>
              <tr>
                <th scope="col">Plugin</th>
                <th scope="col">Risque</th>
                <th scope="col">Attribution</th>
                <th scope="col">Activé</th>
                <th scope="col">Version épinglée</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {plugins.data.map((p) => {
                const asg = pluginBy.get(p.id);
                return (
                  <PluginRow
                    key={`${p.id}:${asg?.enabled}:${asg?.pinnedVersion}`}
                    plugin={p}
                    assignment={asg}
                    busy={action.busy}
                    onSave={(enabled, pinnedVersion) =>
                      void put(() => api.setPluginAssignment(instanceId, p.id, { enabled, pinnedVersion }))
                    }
                    onRemove={() => setRemoving({ kind: 'plugin', plugin: p })}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h3>Packs de skills</h3>
      {packs.data === null && !packs.error && (
        <p className="muted" aria-busy="true">
          Chargement des packs…
        </p>
      )}
      {packs.data?.length === 0 && <p className="muted">Aucun pack de skills dans le catalogue.</p>}
      {packs.data && packs.data.length > 0 && a && (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Packs de skills attribués à l'instance</caption>
            <thead>
              <tr>
                <th scope="col">Pack</th>
                <th scope="col">Attribution</th>
                <th scope="col">Activé</th>
                <th scope="col">Référence épinglée</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {packs.data.map((p) => {
                const asg = packBy.get(p.id);
                return (
                  <PackRow
                    key={`${p.id}:${asg?.enabled}:${asg?.pinnedRef}`}
                    pack={p}
                    assignment={asg}
                    busy={action.busy}
                    onSave={(enabled, pinnedRef) =>
                      void put(() => api.setSkillPackAssignment(instanceId, p.id, { enabled, pinnedRef }))
                    }
                    onRemove={() => setRemoving({ kind: 'pack', pack: p })}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {removing && (
        <ConfirmDialog
          title="Retirer l'attribution"
          confirmLabel="Retirer l'attribution"
          danger
          busy={action.busy}
          onConfirm={() => void confirmRemove()}
          onCancel={() => setRemoving(null)}
        >
          {removing.kind === 'plugin' ? (
            <p>
              Retirer l'attribution de <code>{removing.plugin.package}</code> : l'instance revient au
              comportement par défaut (
              {removing.plugin.enabledByDefault ? 'plugin activé' : 'plugin non servi'}).
            </p>
          ) : (
            <p>
              Retirer l'attribution du pack <strong>{removing.pack.name}</strong> :{' '}
              {removing.pack.mandatory
                ? 'le pack obligatoire reste servi, avec sa référence par défaut.'
                : "il ne sera plus servi et l'agent supprimera son dossier de skills."}
            </p>
          )}
        </ConfirmDialog>
      )}
    </div>
  );
}
